import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { buildApiUrl, getAuthHeaders } from '../../config/api';
import { useRestaurant } from '../../context/RestaurantContext';
import { notify, confirm } from '../../context/FeedbackContext';
import { useParams, useNavigate } from 'react-router-dom';
import './SettingsPage.css';
import { MdTableRestaurant } from 'react-icons/md';
import { FaPrint, FaCalendarAlt, FaFileAlt, FaCreditCard, FaPercent, FaRegTrashAlt, FaEdit, FaExclamationCircle, FaClock } from 'react-icons/fa';
import { getFilteredNavItemsWithIcons, getRouteFromItem } from '../../config/routes';
import HasPermi from '../../components/HasPermi'; // 新增：导入 HasPermi 组件

const tabs = [
  { id: 'PrinterManagement', slug: 'printer-management',    label: 'Printer Management',    icon: <FaPrint /> },
  { id: 'FestivalPrice',     slug: 'is-festival-price',     label: 'Is Festival Price',      icon: <FaCalendarAlt /> },
  { id: 'InvoiceInfo',       slug: 'invoice-info-settings', label: 'Invoice Info Settings',  icon: <FaFileAlt /> },
  { id: 'PaymentType',       slug: 'payment-type',          label: 'Payment Type',           icon: <FaCreditCard /> },
  { id: 'ServiceCharge',     slug: 'service-charge-gst',    label: 'Service Charge & GST',   icon: <FaPercent /> },
  { id: 'NumberOfTables',   slug: 'number-of-tables',      label: 'Number of tables',        icon: <MdTableRestaurant /> },
  { id: 'TableTurnTime',    slug: 'table-turn-time',       label: 'Table Turn Time',          icon: <FaClock /> },
];

// Maps URL slug → tab id
const SLUG_TO_TAB = Object.fromEntries(tabs.map(t => [t.slug, t.id]));

const IPV4_REGEX = /^((25[0-5]|2[0-4]\d|[01]?\d\d?)\.){3}(25[0-5]|2[0-4]\d|[01]?\d\d?)$/;

const EMPTY_FORM = { id: null, printerName: '', ip: '', deviceId: '', paperWidth: '' };
const EMPTY_PRINTER_FORM = {id: null, printerName: '', ip: '', deviceId: '', paperWidth: ''};

const FESTIVAL_DEFAULT = { startDate: '2021-02-09', endDate: '2021-02-09', enabled: true };

const formatFestivalDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

function SettingsPage({ onNavigate }) {
  const { tab: tabSlug } = useParams();
  const { selectedRestaurant } = useRestaurant() || {};
  const settingsNavigate = useNavigate();
  const activeTab = SLUG_TO_TAB[tabSlug] || 'PrinterManagement';
  const [modal, setModal] = useState(null); // null | 'add' | 'edit'
  const [form, setForm] = useState(EMPTY_FORM);
  const [ipError, setIpError] = useState(false);
  const [festivalEditing, setFestivalEditing] = useState(false);
  const [festivalData, setFestivalData] = useState(FESTIVAL_DEFAULT);
  const [festivalForm, setFestivalForm] = useState(FESTIVAL_DEFAULT);
  const [festivalId, setFestivalId] = useState(null);

  const INVOICE_DEFAULT = { name: '', address: '', address2: '', phone: '', uen: '', logo: '' };
  const [invoiceEditing, setInvoiceEditing] = useState(false);
  const [invoiceData, setInvoiceData] = useState(INVOICE_DEFAULT);
  const [invoiceForm, setInvoiceForm] = useState(INVOICE_DEFAULT);
  const [invoiceId, setInvoiceId] = useState(null);
  const [invoiceLogoFile, setInvoiceLogoFile] = useState(null);
  const [invoiceErrors, setInvoiceErrors] = useState({});

  const SC_DEFAULT = { serviceCharge: '', gst: '' };
  const [scEditing, setScEditing] = useState(false);
  const [scData, setScData] = useState(SC_DEFAULT);
  const [scForm, setScForm] = useState(SC_DEFAULT);

  const TABLES_DEFAULT = { qty: 10 };
  const [tableEditing, setTableEditing] = useState(false);
  const [tableCount, setTableCount] = useState(TABLES_DEFAULT.qty);
  const [tableForm, setTableForm] = useState(TABLES_DEFAULT);
  const [tableId, setTableId] = useState(null);

  const TURN_TIME_EMPTY = { hours: '' };
  const [turnTimeEditing, setTurnTimeEditing] = useState(false);
  const [turnTimeHours, setTurnTimeHours] = useState('');
  const [turnTimeForm, setTurnTimeForm] = useState(TURN_TIME_EMPTY);

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const imageInputRef = useRef(null);

  const [paymentEditing, setPaymentEditing] = useState(false);
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [paymentData, setPaymentData] = useState({});
  const [paymentForm, setPaymentForm] = useState({});

  const [view, setView] = useState('list');
  const [printers, setPrinters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  const backToList = () => { setModal(null); setForm(EMPTY_FORM); setFormErrors({}); };

  const handleNavClick = (item) => {
    if (onNavigate) onNavigate(item);
  };

  // Fetching printers from database, filtered by selected restaurant
  const fetchPrinters = async (tenantRef) => {
    if (!tenantRef) return;
    setIsLoading(true);
    try {
      const response = await axios.get(buildApiUrl(`/setting/printers/by-tenant?tenantReference=${tenantRef}`), { headers: getAuthHeaders() });
      setPrinters(response.data.data || []);
    } catch (err) {
      console.error("Failed to fetch printers", err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFestivalPrice = async (tenantRef) => {
    if (!tenantRef) return;
    try {
      const res = await fetch(buildApiUrl(`/setting/festival-price?tenantReference=${tenantRef}`), {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.code === 200 && data.data) {
        const d = data.data;
        const toDateStr = (dt) => {
          if (!dt) return '';
          // ISO format: "2026-05-12T00:00:00"
          if (/^\d{4}-\d{2}-\d{2}/.test(dt)) return dt.substring(0, 10);

          // Global format: "12 May 2026, 00:00" — strip time, parse date part
          const datePart = dt.split(',')[0].trim();
          const d = new Date(datePart);
          if (isNaN(d.getTime())) return '';

          // Get local date components and pad them with leading zeros
          const year = d.getFullYear();
          const month = String(d.getMonth() + 1).padStart(2, '0'); // Months are 0-indexed
          const day = String(d.getDate()).padStart(2, '0');

          return `${year}-${month}-${day}`;
        };
        const mapped = {
          startDate: toDateStr(d.festivalPriceStart),
          endDate: toDateStr(d.festivalPriceEnd),
          enabled: d.status === 1,
        };
        setFestivalData(mapped);
        setFestivalForm(mapped);
        setFestivalId(d.reference || null);
      } else {
        setFestivalData(FESTIVAL_DEFAULT);
        setFestivalForm(FESTIVAL_DEFAULT);
        setFestivalId(null);
      }
    } catch (err) {
      console.error('Failed to fetch festival price', err);
    }
  };

  // Fetching invoice settings
  const fetchInvoiceSettings = async () => {
    if (!selectedRestaurant?.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/setting/invoice?tenantReference=${selectedRestaurant.reference}`), {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.code === 200 && data.data) {
        const d = data.data;
        const mapped = {
          name: d.invoiceName || selectedRestaurant.tenantName || '',
          address: d.invoiceAddressLine1 || '',
          address2: d.invoiceAddressLine2 || '',
          phone: d.invoicePhone || '',
          uen: d.invoiceUen || '',
          logo: d.invoiceLogo || null,
        };
        setInvoiceData(mapped);
        setInvoiceForm(mapped);
        setInvoiceId(d.id || null);
      }
    } catch (err) {
      console.error('Failed to fetch invoice settings', err);
    }
  };

  // Fetching payment types
  const fetchPaymentTypes = async () => {
    if (!selectedRestaurant?.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/setting/payment-types?tenantReference=${selectedRestaurant.reference}`), {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.code === 200 && data.data) {
        setPaymentTypes(data.data);
        const map = {};
        data.data.forEach(p => {
          map[p.paymentName] = p.status === 1;
        });
        setPaymentData(map);
        setPaymentForm(map);
      }
    } catch (err) {
      console.error('Failed to fetch payment types', err);
    }
  };

  // Fetching service charge & GST
  const fetchScGst = async () => {
    if (!selectedRestaurant?.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/setting/sc-gst?tenantReference=${selectedRestaurant.reference}`), {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.code === 200 && data.data) {
        const sc = {
          serviceCharge: data.data.serviceCharge !== null ? String(data.data.serviceCharge) : '',
          gst: data.data.gst !== null ? String(data.data.gst) : '',
        };
        setScData(sc);
        setScForm(sc);
      }
    } catch (err) {
      console.error('Failed to fetch SC/GST', err);
    }
  };

  // Fetching table quantity settings
  const fetchTableSettings = async () => {
    if (!selectedRestaurant?.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/setting/tables/quantity?tenantReference=${selectedRestaurant.reference}`), {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.code === 200 && data.data) {
        const d = data.data;
        const mapped = {
          qty: d.qty,
          reference: d.reference
        };
        setTableCount(mapped.qty);
        setTableForm(mapped);
        setTableId(d.id || null);
      }
    } catch (err) {
      console.error('Failed to fetch table quantity settings', err);
    }
  }

  // 接口 turnTime 为分钟，页面按小时展示；无效或未配置则不显示数字
  const minutesToHours = (minutes, fallbackHours = '') => {
    const n = Number(minutes);
    if (!Number.isFinite(n) || n < 1) return fallbackHours;
    return Math.max(1, Math.round(n / 60));
  };

  // GET /setting/config，读取 data.reservation.turnTime（分钟）
  const fetchTurnTimeSettings = async () => {
    if (!selectedRestaurant?.reference) return;
    try {
      const res = await fetch(buildApiUrl(`/setting/config?tenantReference=${selectedRestaurant.reference}`), {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (data.code === 200 && data.data?.reservation?.turnTime != null && data.data.reservation.turnTime !== '') {
        const hours = minutesToHours(data.data.reservation.turnTime);
        setTurnTimeHours(hours);
        setTurnTimeForm({ hours });
      } else {
        setTurnTimeHours('');
        setTurnTimeForm(TURN_TIME_EMPTY);
      }
    } catch (err) {
      console.error('Failed to fetch table turn time settings', err);
      setTurnTimeHours('');
      setTurnTimeForm(TURN_TIME_EMPTY);
    }
  };

  useEffect(() => {
    if (!selectedRestaurant?.reference) return;
    const ref = selectedRestaurant.reference;
    switch (activeTab) {
      case 'PrinterManagement':
        fetchPrinters(ref);
        break;
      case 'FestivalPrice':
        fetchFestivalPrice(ref);
        break;
      case 'InvoiceInfo':
        fetchInvoiceSettings();
        break;
      case 'PaymentType':
        fetchPaymentTypes();
        break;
      case 'ServiceCharge':
        fetchScGst();
        break;
      case 'NumberOfTables':
        fetchTableSettings();
        break;
      case 'TableTurnTime':
        fetchTurnTimeSettings();
        break;
      default:
        break;
    }
  }, [selectedRestaurant, activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Setting of Printer Creation
  const openAddPrinter = () => {
    setForm({
      printerName: '',
      ip: '',
      deviceId: '',
      paperWidth: ''
    });
    setFormErrors({});
    setModal('add');
  };

  const openEditPrinter = (printer) => {
    setForm({
      reference: printer.reference,
      printerName: printer.printerName,
      ip: printer.ip,
      deviceId: printer.deviceId || '',
      paperWidth: printer.paperWidth || ''
    });
    setIpError(false);
    setFormErrors({});
    setModal('edit');
  };

  // Creating a printer
  const handleCreatePrinter = async () => {
    const printerDtoData = {
      printerName: form.printerName,
      ip: form.ip,
      deviceId: form.deviceId,
      paperWidth: form.paperWidth,
      tenantReference: selectedRestaurant?.reference,
    };

    try {
      const response = await fetch(buildApiUrl('/setting/printers/create'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(printerDtoData),
      });

      const data = await response.json();

      if (data.code === 200) {
        notify(data.message || 'Printer added successfully!', 'success');
        fetchPrinters(selectedRestaurant?.reference);
        backToList();
      } else {
        notify(data.message || 'Failed to add printer.', 'error');
      }
    } catch (err) {
      console.error('Failed to add printer.', err);
    }
  };

  // Editing a printer
  const handleEditPrinter = async () => {
    const submitData = new FormData();

    const printerDtoData = {
      reference: form.reference,
      printerName: form.printerName,
      ip: form.ip,
      deviceId: form.deviceId,
      paperWidth: form.paperWidth
    };

    console.log("请求参数",form)
    console.log("请求参数",printerDtoData)

    const printerBlob = new Blob(
      [JSON.stringify(printerDtoData)],
      {type: 'application/json'}
    );

    // Append the Blob using the exact name expected by @RequestPart 
    submitData.append('printer', printerBlob);

    try {
      const response = await fetch(buildApiUrl('/setting/printers/edit'), {
        method: 'POST',
        headers: {
           'Content-Type': 'application/json',
          ...getAuthHeaders() },
        body: JSON.stringify(printerDtoData),
      });

      const data = await response.json();

      if (data.code === 200) {
        notify(data.message || 'Printer edited successfully!', 'success');
        fetchPrinters(selectedRestaurant?.reference);
        backToList();
        setForm(EMPTY_FORM);
      } else {
        notify(data.message || 'Failed to edit printer.', 'error');
      }
    } catch (err) {
      console.error('Failed to edit printer', err);
    }
  }

  const closeModal = () => {
    setModal(null);
    setForm(EMPTY_FORM);
    setIpError(false);
  };

  //For printer
  const handleFormChange = (e) => {
    setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    setFormErrors(prev => ({ ...prev, [e.target.name]: undefined }));
  };

  const openFestivalEdit = () => {
    setFestivalForm({ ...festivalData });
    setFestivalEditing(true);
  };

  const cancelFestivalEdit = () => setFestivalEditing(false);

  const handleFestivalFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFestivalForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  // For festival price
  const saveFestivalChanges = async () => {
    if (!selectedRestaurant?.reference) { notify('No restaurant selected.', 'error'); return; }
 
    const festivalPriceDtoData = {
      reference: festivalId,
      festivalPriceStart: festivalForm.startDate ? `${festivalForm.startDate}T00:00:00` : null,
      festivalPriceEnd: festivalForm.endDate ? `${festivalForm.endDate}T23:59:59` : null,
      tenantReference: selectedRestaurant.reference,
      status: festivalForm.enabled ? 1 : 0,
    };

    try {
      const response = await fetch(buildApiUrl('/setting/festival-price/update'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(festivalPriceDtoData)
      });

      const data = await response.json();

      if (data.code === 200) {
        notify(data.message || 'Festival price saved successfully!', 'success');
        setFestivalEditing(false);
        await fetchFestivalPrice(selectedRestaurant.reference);
      } else {
        notify(data.message || 'Failed to save festival price.', 'error');
      }
    } catch (err) {
      console.error('Failed to save festival price.', err);
    }
  };

  const openInvoiceEdit = () => {
    setInvoiceForm({ ...invoiceData });
    setInvoiceEditing(true);
  };

  const cancelInvoiceEdit = () => { setInvoiceEditing(false); setInvoiceErrors({}); };

  const validateInvoice = () => {
    const errors = {};
    if (!invoiceForm.name.trim()) errors.name = 'Restaurant name is required.';
    if (!invoiceForm.address.trim()) errors.address = 'Address is required.';
    if (!invoiceForm.phone.trim()) errors.phone = 'Phone number is required.';
    else if (!/^[\d+\-\s()]+$/.test(invoiceForm.phone.trim())) errors.phone = 'Phone number contains invalid characters.';
    setInvoiceErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Submit invoice setting to backend 
  const saveInvoiceChanges = async () => {
    if (!validateInvoice()) return;
    if (!selectedRestaurant?.reference) { notify('No restaurant selected.', 'error'); return; }
    const invoiceDto = {
      id: invoiceId,
      tenantReference: selectedRestaurant.reference,
      invoiceName: invoiceForm.name,
      invoiceAddressLine1: invoiceForm.address,
      invoiceAddressLine2: invoiceForm.address2,
      invoicePhone: invoiceForm.phone,
      invoiceUen: invoiceForm.uen,
    };
    const invoiceBlob = new Blob([JSON.stringify(invoiceDto)], { type: 'application/json' });
    const formData = new FormData();
    formData.append('invoice', invoiceBlob);
    if (invoiceLogoFile) formData.append('image', invoiceLogoFile);
    try {
      const res = await fetch(buildApiUrl('/setting/invoice/update'), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: formData,
      });
      const data = await res.json();
      if (data.code === 200) {
        notify('Invoice settings saved successfully!', 'success');
        setInvoiceLogoFile(null);
        setInvoiceEditing(false);
        await fetchInvoiceSettings();
      } else {
        notify('Failed to save invoice settings: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Failed to save invoice settings', err);
      notify('Failed to save invoice settings: ' + err.message, 'error');
    }
  };

  // Submit table qty setting to backend 
  const handleSaveTable = async () => {
    if (!selectedRestaurant?.reference) { notify('No restaurant selected.', 'error'); return; }
    const tableDto = {
      reference:tableForm.reference,
      tenantReference: selectedRestaurant.reference,
      qty: tableForm.qty
    };
    const tableBlob = new Blob([JSON.stringify(tableDto)], { type: 'application/json' });
    const formData = new FormData();
    formData.append('tables', tableBlob);
  
    try {
      const res = await fetch(buildApiUrl('/setting/tables/update'), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: formData,
      });
      const data = await res.json();
      if (data.code === 200) {
        notify('Table quantity settings saved successfully!', 'success');
        setTableEditing(false);
        await fetchTableSettings();
      } else {
        notify('Failed to save table quantity settings: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Failed to save table quantity settings', err);
      notify('Failed to save table quantity settings: ' + err.message, 'error');
    }
  }

  // 保存预约窗口时间：PUT /setting/table-turn-time/update，入参 turnTime 为分钟
  const handleSaveTurnTime = async () => {
    if (!selectedRestaurant?.reference) { notify('No restaurant selected.', 'error'); return; }
    const hours = parseInt(turnTimeForm.hours, 10);
    if (!Number.isFinite(hours) || hours < 1) {
      notify('Please enter a turn time of at least 1 hour.', 'error');
      return;
    }
    // 页面填小时，接口存分钟，例如 3 → 180
    const turnTimeMinutes = hours * 60;
    try {
      const res = await fetch(buildApiUrl('/setting/table-turn-time/update'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          tenantReference: selectedRestaurant.reference,
          turnTime: turnTimeMinutes,
        }),
      });
      const data = await res.json();
      if (data.code === 200) {
        // 用接口返回的分钟数回写页面，避免和库里实际值不一致
        const savedHours = minutesToHours(data.data?.turnTime, hours);
        setTurnTimeHours(savedHours);
        setTurnTimeForm({ hours: savedHours });
        setTurnTimeEditing(false);
        notify(data.msg || 'Operation is successful', 'success');
      } else {
        notify(data.msg || 'Failed to save table turn time', 'error');
      }
    } catch (err) {
      console.error('Failed to save table turn time', err);
      notify('Failed to save table turn time: ' + err.message, 'error');
    }
  };


  // Handle Invoice Change
  const handleInvoiceFormChange = (e) => {
    const { name, value } = e.target;
    setInvoiceForm(prev => ({ ...prev, [name]: value }));
    setInvoiceErrors(prev => ({ ...prev, [name]: undefined }));
  };

  const openScEdit = () => { setScForm({ ...scData }); setScEditing(true); };
  const cancelScEdit = () => setScEditing(false);
  const saveScChanges = async () => {
    if (!selectedRestaurant?.reference) { notify('No restaurant selected.', 'error'); return; }
    try {
      const res = await fetch(buildApiUrl('/setting/sc-gst/save'), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          tenantReference: selectedRestaurant.reference,
          serviceCharge: parseFloat(scForm.serviceCharge) || 0,
          gst: parseFloat(scForm.gst) || 0,
        }),
      });
      const data = await res.json();
      if (data.code === 200) {
        setScData({ ...scForm });
        setScEditing(false);
      } else {
        notify('Failed to save: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Failed to save SC/GST', err);
      notify('Failed to save: ' + err.message, 'error');
    }
  };
  const handleScChange = (e) => {
    const { name, value } = e.target;
    setScForm(prev => ({ ...prev, [name]: value }));
  };

  const openPaymentEdit = () => { setPaymentForm({ ...paymentData }); setPaymentEditing(true); };
  const cancelPaymentEdit = () => setPaymentEditing(false);
  const savePaymentChanges = async () => {
    if (!selectedRestaurant?.reference) { notify('No restaurant selected.', 'error'); return; }
    const payload = paymentTypes.map(p => ({
      paymentName: p.paymentName,
      status: paymentForm[p.paymentName] ? 1 : 0,
    }));
    try {
      const res = await fetch(buildApiUrl(`/setting/payment-types/save?tenantReference=${selectedRestaurant.reference}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.code === 200) {
        setPaymentData({ ...paymentForm });
        setPaymentEditing(false);
      } else {
        notify('Failed to save: ' + (data.msg || 'Unknown error'), 'error');
      }
    } catch (err) {
      console.error('Failed to save payment types', err);
      notify('Failed to save: ' + err.message, 'error');
    }
  };
  const handlePaymentChange = (method) => {
    setPaymentForm(prev => ({ ...prev, [method]: !prev[method] }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setInvoiceLogoFile(file);
      setInvoiceForm(prev => ({ ...prev, logo: URL.createObjectURL(file) }));
    }
  };

  // For printer
  const handleSavePrinter = () => {
    const userRole = localStorage.getItem('role');

    const isDuplicate = printers.some(p => p.ip === form.ip && p.id != form.id);
    if (!IPV4_REGEX.test(form.ip) || isDuplicate) {
      setIpError(true);
      return;
    }

    // if (userRole != 'Owner') {
    //   notify('You do not have permission to manage printers for this restaurant!', 'error');
    //   return;
    // }

    if (modal === 'add') {
      handleCreatePrinter();
    }

    else if (modal === 'edit') {
      handleEditPrinter();
    }

    // Backend call goes here
    closeModal();
  }; 

  // For deleting printer 
  const handleDeletePrinter = async (printerToDelete) => {
    if (!(await confirm('Are you sure you want to delete this printer?'))) return; 
    // const submitData = new FormData();

    // submitData.append('reference', printerToDelete.reference)
    // const printerDtoData = {
    //   d: printerToDelete.id, // Using the ID from the passed printer object
    // }

    // const printerBlob = new Blob(
    //   [JSON.stringify(printerDtoData)],
    //   {type: 'application/json'}
    // );

    // submitData.append('printer', printerBlob);
    // console.log("submitData=====>",submitData);

    try {
      const response = await fetch(buildApiUrl('/setting/printers/delete'), {
        method: 'POST',
        headers: { ...getAuthHeaders(),
           'Content-Type': 'application/json',
         },
       body: JSON.stringify({
        reference: printerToDelete.reference,
      }),
      });

      const data = await response.json();
      if (data.code === 200) {
        notify(data.message || 'Printer deleted successfully!', 'success');
        setPrinters(prev => prev.filter(p => p.id !== printerToDelete.id));
        await fetchPrinters(selectedRestaurant?.reference);
      } else {
      notify(data.message || 'Failed to delete printer.', 'error');
      }
    } catch (err) {
      console.error('Failed to delete printer.', err);
    }
  };

  const { items: navItems, icons: navIcons } = getFilteredNavItemsWithIcons();

  return (
    <div className="settings-wrapper">
      <nav className="sidebar">
        <div className="sidebar-title">Restaurant Management</div>
        <ul>
          {navItems.map(item =>
            (
              <li
                key={item}
                className={item === 'Settings' ? 'active' : ''}
                onClick={() => handleNavClick(item)}
              >
                <span className="nav-icon">{navIcons[item]}</span> {item}
              </li>
            )
          )}
        </ul>
      </nav>

      <main className="settings-main">
        <h1 className="settings-title">Settings</h1>
        <p className="settings-subtitle">Configure system-wide options and administrative preferences.</p>

        {/* Tab bar */}
        <div className="settings-tabs">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`settings-tab${activeTab === tab.id ? ' active' : ''}`}
              onClick={() => {
                setFestivalEditing(false);
                setInvoiceEditing(false);
                setPaymentEditing(false);
                setScEditing(false);
                setTableEditing(false);
                setTurnTimeEditing(false);
                settingsNavigate(`/settings/${tab.slug}`);
              }}
            >
              <span className="tab-icon">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        <div className={`settings-content${['FestivalPrice','InvoiceInfo','PaymentType','ServiceCharge','NumberOfTables','TableTurnTime'].includes(activeTab) ? ' settings-content-flat' : ''}`}>

          {activeTab === 'PrinterManagement' && (
            <>
              <div className="settings-section-header">
                <div>
                  <h2 className="settings-section-title">Printer Management</h2>
                  <p className="settings-section-subtitle">Manage receipt printers — add, edit or remove printers</p>
                </div>
                {/* 添加打印机按钮 - 需要 settings:printers:create 权限 */}
                <HasPermi permission="settings:printers:create">
                  <button className="settings-add-btn" onClick={openAddPrinter}>+ Add Printer</button>
                </HasPermi>
              </div>

              <div className="settings-table-wrap">
                <table className="settings-table">
                  <thead>
                    <tr>
                      <th>Printer Name</th>
                      <th>IP Address</th>
                      <th>Device ID</th>
                      <th>Paper Width</th>
                      <th className="settings-th-actions">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {printers.map(printer => (
                      <tr key={printer.id}>
                        <td>{printer.printerName}</td>
                        <td>{printer.ip}</td>
                        <td>{printer.deviceId || '—'}</td>
                        <td>{printer.paperWidth || '—'}</td>
                        <td className="settings-actions-cell">
                          <HasPermi permission="settings:printers:edit">
                            <button className="settings-edit-btn" onClick={() => openEditPrinter(printer)}><FaEdit /></button>
                          </HasPermi>
                          <HasPermi permission="settings:printers:delete">
                            <button className="settings-delete-btn" onClick={() => handleDeletePrinter(printer)}><FaRegTrashAlt /></button>
                          </HasPermi>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="settings-table-footer">Showing {printers.length} printers</div>
            </>
          )}

          {activeTab === 'FestivalPrice' && (
            <div className="festival-wrap">
              <div className="festival-header">
                <h2 className="settings-section-title">
                  Is Festival Price
                  {/* 编辑图标 - 需要 settings:festival-price:update 权限 */}
                  <HasPermi permission="settings:festival-price:update">
                    {!festivalEditing && (
                      <FaEdit className="festival-edit-icon" onClick={openFestivalEdit} />
                    )}
                  </HasPermi>
                </h2>
                <p className="settings-section-subtitle">Manage festival price — You can set a date range and use the festival price</p>
              </div>

              <div className="festival-date-range">
                {festivalEditing ? (
                  <>
                    <div className="festival-date-input-wrap">
                      <FaCalendarAlt className="festival-date-icon" />
                      <div className="festival-date-display-wrap">
                        <span className="festival-date-display">{formatFestivalDate(festivalForm.startDate)}</span>
                        <input
                          type="date"
                          name="startDate"
                          className="festival-date-input-hidden"
                          value={festivalForm.startDate}
                          onChange={handleFestivalFormChange}
                        />
                      </div>
                    </div>
                    <span className="festival-date-sep">—</span>
                    <div className="festival-date-input-wrap">
                      <FaCalendarAlt className="festival-date-icon" />
                      <div className="festival-date-display-wrap">
                        <span className="festival-date-display">{formatFestivalDate(festivalForm.endDate)}</span>
                        <input
                          type="date"
                          name="endDate"
                          className="festival-date-input-hidden"
                          value={festivalForm.endDate}
                          onChange={handleFestivalFormChange}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="festival-date-btn">
                      <FaCalendarAlt className="festival-date-icon" />
                      {formatFestivalDate(festivalData.startDate)}
                    </div>
                    <span className="festival-date-sep">—</span>
                    <div className="festival-date-btn">
                      <FaCalendarAlt className="festival-date-icon" />
                      {formatFestivalDate(festivalData.endDate)}
                    </div>
                  </>
                )}
              </div>

              <label className="festival-toggle">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={festivalEditing ? festivalForm.enabled : festivalData.enabled}
                  onChange={festivalEditing ? handleFestivalFormChange : undefined}
                  readOnly={!festivalEditing}
                />
                <span className="festival-toggle-slider" />
              </label>

              {festivalEditing && (
                <div className="festival-actions">
                  <button className="festival-cancel-btn" onClick={cancelFestivalEdit}>Cancel</button>
                  <button className="festival-save-btn" onClick={saveFestivalChanges}>Save Changes</button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'InvoiceInfo' && (
            <div className="invoice-wrap">
              <div className="invoice-header">
                <h2 className="settings-section-title">
                  Invoice Info Settings
                  {/* 编辑图标 - 需要 settings:invoice:update 权限 */}
                  <HasPermi permission="settings:invoice:update">
                    {!invoiceEditing && (
                      <FaEdit className="festival-edit-icon" onClick={openInvoiceEdit} />
                    )}
                  </HasPermi>
                </h2>
                <p className="settings-section-subtitle">Configure how your business appears on printed and digital invoices.</p>
              </div>

              <div className="invoice-field">
                <label className="invoice-label">Restaurant Name</label>
                <input
                  className={`invoice-input${invoiceEditing ? '' : ' invoice-input-readonly'}${invoiceErrors.name ? ' invoice-input-error' : ''}`}
                  type="text"
                  name="name"
                  value={invoiceEditing ? invoiceForm.name : invoiceData.name}
                  onChange={invoiceEditing ? handleInvoiceFormChange : undefined}
                  readOnly={!invoiceEditing}
                />
                {invoiceErrors.name && <p className="invoice-error">{invoiceErrors.name}</p>}
              </div>

              <div className="invoice-field">
                <label className="invoice-label">Restaurant Logo</label>
                <div className="invoice-logo-wrap">
                  <div className="invoice-logo-box">
                    {(invoiceEditing ? invoiceForm.logo : invoiceData.logo) ? (
                      <img
                        src={
                          invoiceEditing && invoiceLogoFile
                            ? invoiceForm.logo // Use the local 'blob:' URL for the preview
                            : (invoiceEditing ? invoiceForm.logo : invoiceData.logo)
                        }
                        alt="logo"
                        className="invoice-logo-img"
                      />
                    ) : (
                      <span className="invoice-logo-empty" />
                    )}
                  </div>
                  {invoiceEditing && (
                    <label className="invoice-logo-edit-btn">
                      <FaEdit />
                      <input
                        ref={imageInputRef}
                        type="file"
                        accept="image/*"
                        className="invoice-logo-input"
                        onChange={handleLogoChange} />
                    </label>
                  )}
                </div>
              </div>

              <div className="invoice-field">
                <label className="invoice-label">Restaurant Address 1</label>
                <textarea
                  className={`invoice-textarea${invoiceEditing ? '' : ' invoice-input-readonly'}${invoiceErrors.address ? ' invoice-input-error' : ''}`}
                  name="address"
                  value={invoiceEditing ? invoiceForm.address : invoiceData.address}
                  onChange={invoiceEditing ? handleInvoiceFormChange : undefined}
                  readOnly={!invoiceEditing}
                  rows={3}
                />
                {invoiceErrors.address && <p className="invoice-error">{invoiceErrors.address}</p>}
              </div>

              <div className="invoice-field">
                <label className="invoice-label">Restaurant Address 2</label>
                <textarea
                  className={`invoice-textarea${invoiceEditing ? '' : ' invoice-input-readonly'}`}
                  name="address2"
                  value={invoiceEditing ? invoiceForm.address2 : invoiceData.address2}
                  onChange={invoiceEditing ? handleInvoiceFormChange : undefined}
                  readOnly={!invoiceEditing}
                  rows={3}
                />
              </div>

              <div className="invoice-row">
                <div className="invoice-field">
                  <label className="invoice-label">Phone Number</label>
                  <input
                    className={`invoice-input${invoiceEditing ? '' : ' invoice-input-readonly'}${invoiceErrors.phone ? ' invoice-input-error' : ''}`}
                    type="text"
                    name="phone"
                    value={invoiceEditing ? invoiceForm.phone : invoiceData.phone}
                    onChange={invoiceEditing ? handleInvoiceFormChange : undefined}
                    readOnly={!invoiceEditing}
                  />
                  {invoiceErrors.phone && <p className="invoice-error">{invoiceErrors.phone}</p>}
                </div>
                <div className="invoice-field">
                  <label className="invoice-label">UEN</label>
                  <input
                    className={`invoice-input${invoiceEditing ? '' : ' invoice-input-readonly'}`}
                    type="text"
                    name="uen"
                    value={invoiceEditing ? invoiceForm.uen : invoiceData.uen}
                    onChange={invoiceEditing ? handleInvoiceFormChange : undefined}
                    readOnly={!invoiceEditing}
                  />
                </div>
              </div>

              {invoiceEditing && (
                <div className="invoice-actions">
                  <button className="festival-cancel-btn" onClick={cancelInvoiceEdit}>Cancel</button>
                  <button className="festival-save-btn" onClick={saveInvoiceChanges}>Save Changes</button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'PaymentType' && (
            <div className="payment-wrap">
              <div className="invoice-header">
                <h2 className="settings-section-title">
                  Payment Type
                  {/* 编辑图标 - 需要 settings:payment-types:save 权限 */}
                  <HasPermi permission="settings:payment-types:save">
                    {!paymentEditing && (
                      <FaEdit className="festival-edit-icon" onClick={openPaymentEdit} />
                    )}
                  </HasPermi>
                </h2>
                <p className="settings-section-subtitle">Set up supported payment type</p>
              </div>

              <div className={`payment-list${paymentEditing ? ' payment-list-editing' : ''}`}>
                {paymentTypes.map(p => (
                  <label key={p.paymentName} className={`payment-item${paymentEditing ? ' payment-item-active' : ''}`}>
                    <input
                      type="checkbox"
                      className="payment-checkbox"
                      checked={paymentEditing ? paymentForm[p.paymentName] : paymentData[p.paymentName]}
                      onChange={paymentEditing ? () => handlePaymentChange(p.paymentName) : () => {}}
                    />
                    <span className="payment-label">{p.paymentName}</span>
                  </label>
                ))}
              </div>

              {paymentEditing && (
                <div className="invoice-actions">
                  <button className="festival-cancel-btn" onClick={cancelPaymentEdit}>Cancel</button>
                  <button className="festival-save-btn" onClick={savePaymentChanges}>Save Changes</button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'ServiceCharge' && (
            <div className="sc-wrap">
              <div className="invoice-header">
                <h2 className="settings-section-title">
                  Service Charge &amp; GST
                  {/* 编辑图标 - 需要 settings:sc-gst:save 权限 */}
                  <HasPermi permission="settings:sc-gst:save">
                    {!scEditing && <FaEdit className="festival-edit-icon" onClick={openScEdit} />}
                  </HasPermi>
                </h2>
                <p className="settings-section-subtitle">Set up service Charge and GST</p>
              </div>

              <div className="sc-field">
                <label className="invoice-label">Service Charge</label>
                <div className="sc-input-wrap">
                  <input
                    className={`sc-input${scEditing ? '' : ' invoice-input-readonly'}`}
                    type="number"
                    name="serviceCharge"
                    value={scEditing ? scForm.serviceCharge : scData.serviceCharge}
                    onChange={scEditing ? handleScChange : undefined}
                    readOnly={!scEditing}
                  />
                  <span className="sc-percent">%</span>
                </div>
              </div>

              <div className="sc-field">
                <label className="invoice-label">GST</label>
                <div className="sc-input-wrap">
                  <input
                    className={`sc-input${scEditing ? '' : ' invoice-input-readonly'}`}
                    type="number"
                    name="gst"
                    value={scEditing ? scForm.gst : scData.gst}
                    onChange={scEditing ? handleScChange : undefined}
                    readOnly={!scEditing}
                  />
                  <span className="sc-percent">%</span>
                </div>
              </div>

              {scEditing && (
                <div className="invoice-actions">
                  <button className="festival-cancel-btn" onClick={cancelScEdit}>Cancel</button>
                  <button className="festival-save-btn" onClick={saveScChanges}>Save Changes</button>
                </div>
              )}
            </div>
          )}

          {activeTab === 'NumberOfTables' && (
            <div className="sc-wrap">
              <div className="invoice-header">
                <h2 className="settings-section-title">
                  Number of tables
                  {/* 编辑图标 - 需要 settings:tables:update 权限 */}
                  <HasPermi permission="settings:tables:update">
                    {!tableEditing && (
                      <FaEdit className="festival-edit-icon" onClick={() => {
                        setTableForm({ ...tableForm, qty: tableCount });
                        setTableEditing(true);
                      }} />
                    )}
                  </HasPermi>
                </h2>
                <p className="settings-section-subtitle">Set the number of tables for your restaurant</p>
              </div>

              <div className="sc-field">
                <div className="sc-input-wrap">
                  <input
                    className={`sc-input tables-input${tableEditing ? '' : ' invoice-input-readonly'}`}
                    type="number"
                    min="1"
                    value={tableEditing ? tableForm.qty : tableCount}
                    onChange={tableEditing ? e => setTableForm({ ...tableForm, qty: Math.max(1, parseInt(e.target.value) || 1) }) : undefined}
                    readOnly={!tableEditing}
                  />
                </div>
              </div>

              {tableEditing && (
                <div className="invoice-actions">
                  <button className="festival-cancel-btn" onClick={() => setTableEditing(false)}>Cancel</button>
                  <button className="festival-save-btn" onClick={handleSaveTable}>Save Changes</button>
                </div>
              )}
            </div>
          )}

          {/* 预约窗口时间：页面按小时编辑，保存时在 handleSaveTurnTime 里换成分钟再 PUT */}
          {activeTab === 'TableTurnTime' && (
            <div className="sc-wrap">
              <div className="invoice-header">
                <h2 className="settings-section-title">
                  Table Turn Time
                  {/* 编辑图标：有 table-turn-time 或 tables 的更新权限才显示 */}
                  <HasPermi permission={['settings:table-turn-time:update', 'settings:tables:update']} any>
                    {!turnTimeEditing && (
                      <FaEdit className="festival-edit-icon" onClick={() => {
                        setTurnTimeForm({ hours: turnTimeHours });
                        setTurnTimeEditing(true);
                      }} />
                    )}
                  </HasPermi>
                </h2>
                <p className="settings-section-subtitle">Reservation window — a table cannot be booked again within this period of an existing reservation</p>
              </div>

              {/* 只读显示已保存值，未配置时留空；编辑时允许清空，最少 1 小时才能保存 */}
              <div className="sc-field">
                <label className="invoice-label">Turn time</label>
                <div className="sc-input-wrap turn-time-input-wrap">
                  <input
                    className={`sc-input${turnTimeEditing ? '' : ' invoice-input-readonly'}`}
                    type="number"
                    min="1"
                    step="1"
                    value={turnTimeEditing ? turnTimeForm.hours : turnTimeHours}
                    placeholder=""
                    onChange={turnTimeEditing ? e => {
                      const v = e.target.value;
                      setTurnTimeForm({ hours: v === '' ? '' : Math.max(1, parseInt(v, 10) || 1) });
                    } : undefined}
                    readOnly={!turnTimeEditing}
                  />
                  <span className="sc-percent">hours</span>
                </div>
              </div>

              {/* 编辑态才出现：Cancel 丢弃未保存修改，Save 调用 PUT /setting/table-turn-time/update */}
              {turnTimeEditing && (
                <div className="invoice-actions">
                  <button className="festival-cancel-btn" onClick={() => setTurnTimeEditing(false)}>Cancel</button>
                  <button className="festival-save-btn" onClick={handleSaveTurnTime}>Save Changes</button>
                </div>
              )}
            </div>
          )}

        </div>
      </main>

      {/* Printer Modal - 需要 settings:printers:create 或 settings:printers:edit 权限 */}
      <HasPermi permission={['settings:printers:create', 'settings:printers:edit']} any>
        {modal && (
          <div className="printer-modal-overlay" onClick={closeModal}>
            <div className="printer-modal" onClick={e => e.stopPropagation()}>
              <div className="printer-modal-header">
                <h2 className="printer-modal-title">
                  {modal === 'add' ? 'Add New Printer' : 'Edit Printer'}
                </h2>
              </div>
              <div className="printer-modal-body">
                <div className="printer-modal-field">
                  <label className="printer-modal-label">Printer Name</label>
                  <input
                    className="printer-modal-input"
                    type="text"
                    name="printerName"
                    value={form.printerName}
                    placeholder={modal === 'add' ? 'e.g., Main Kitchen Receipt' : ''}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="printer-modal-field">
                  <label className="printer-modal-label">IP Address</label>
                  <div className="printer-modal-input-wrap">
                    <input
                      className={`printer-modal-input${ipError ? ' printer-modal-input-error' : ''}`}
                      type="text"
                      name="ip"
                      value={form.ip}
                      placeholder={modal === 'add' ? 'e.g., 192.168.1.50' : ''}
                      onChange={handleFormChange}
                    />
                    {ipError && <FaExclamationCircle className="printer-modal-input-icon-error" />}
                  </div>
                  {ipError && (
                    <p className="printer-modal-error">Please enter a valid IPv4 address.</p>
                  )}
                </div>
                <div className="printer-modal-field">
                  <label className="printer-modal-label">Device ID</label>
                  <input
                    className="printer-modal-input"
                    type="text"
                    name="deviceId"
                    value={form.deviceId}
                    placeholder={modal === 'add' ? 'e.g., local_printer' : ''}
                    onChange={handleFormChange}
                  />
                </div>
                <div className="printer-modal-field">
                  <label className="printer-modal-label">Paper Width (mm)</label>
                  <input
                    className="printer-modal-input"
                    type="text"
                    name="paperWidth"
                    value={form.paperWidth}
                    placeholder={modal === 'add' ? 'e.g., 80' : ''}
                    onChange={handleFormChange}
                  />
                </div>
              </div>
              <div className="printer-modal-footer">
                <button className="printer-modal-cancel" onClick={closeModal}>Cancel</button>
                <button className="printer-modal-save" onClick={handleSavePrinter}>Save Printer</button>
              </div>
            </div>
          </div>
        )}
      </HasPermi>
    </div>
  );
}

export default SettingsPage;
